import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useListBrowserUtils } from '@ts-private/list-browser';
import { dictionaryDetailsStore } from './DictionaryDetailListBrowser';
import { useTranslation } from 'react-i18next';
import { Portal, TableCell, TableRow } from '@mui/material';
import { get, sequence, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import DictionaryDetailItemComponent from './DictionaryDetailItemComponent';
import { IconButton, Typography } from '@totalsoft/rocket-ui';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import { concatFailure, items, required, shape, unique } from '@totalsoft/pure-validations';
import { getErrors, useValidation } from '@totalsoft/pure-validations-react';
import { StyledTableRow } from 'components/common/Styles';
import { ThemedWrapper } from 'components/common/SurfaceStyles';
import { Prompt } from 'react-router-dom';
import { useToast } from 'hooks/toasts/useToast';
import { UPDATE_DICTIONARY_ITEMS, UPDATE_GENERIC_DICTIONARY_ITEMS } from '../mutations';
import { DICTIONARY_CHANGED } from '../subscriptions';
import { useMutation, useSubscription } from 'hooks/apollo';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_DICTIONARIES, ADMIN_DICTIONARIES_WRITE } from 'constants/rightsCodes';

const validator = concatFailure(
  unique(x => x.code || {}, 'Dictionaries.DictionaryItem.Validators.CodeUnique'),
  unique(x => x.implicit || {}, 'Dictionaries.DictionaryItem.Validators.ImplicitUnique'),
  items(shape({ code: required, name: required }))
);

const getEmptyGenericDictionaryItem = () => ({
  id: crypto.randomUUID(),
  code: '',
  name: '',
  description: '',
  implicit: false,
  isActive: false,
  isNew: true,
  originalIndex: 0
});

const getEmptyDictionaryItem = () => ({
  id: crypto.randomUUID(),
  code: '',
  name: '',
  isActive: false,
  isNew: true,
  originalIndex: 0
});

const DictionaryDetailListComponent = ({ dictionary, withDictionaryItem, columnsLength }) => {
  const { t } = useTranslation();
  const addToast = useToast();

  const canRead = usePermission(ADMIN_DICTIONARIES);
  const canWrite = usePermission(ADMIN_DICTIONARIES_WRITE);
  const { store } = useListBrowserUtils(dictionaryDetailsStore);

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens(store.allValues.map((value, index) => ({ ...value, originalIndex: index })));
  const [validation, validate] = useValidation(validator);

  const [pending, setPending] = useState(false);

  useEffect(() => {
    resetLens(store.allValues.map((value, index) => ({ ...value, originalIndex: index })));
  }, [store.allValues, resetLens]);

  const getNewDetail = useMemo(() => (withDictionaryItem ? getEmptyDictionaryItem : getEmptyGenericDictionaryItem), [withDictionaryItem]);

  const getDetails = () => {
    let newDetails = [],
      existingDetails = [];
    sequence(lens).forEach(detailLens => {
      if (detailLens.isNew |> get) newDetails.push(detailLens);
      if (store.values.some(item => item.id === (detailLens.id |> get))) existingDetails.push(detailLens);
    });
    // "Sort" the existing details to reflect the order in the store values
    existingDetails = store.values.map(item => existingDetails.find(detailLens => (detailLens.id |> get) === item.id));
    return [...newDetails, ...existingDetails];
  };

  const handleAdd = () => {
    const current = lens |> get;
    (lens |> set)([getNewDetail(), ...current.map(value => ({ ...value, originalIndex: value.originalIndex + 1 }))]);
  };

  const dictionaryId = useMemo(() => (withDictionaryItem ? dictionary.dictionaryId : dictionary.id), [withDictionaryItem, dictionary]);

  // #region Save

  const [updateDetails] = useMutation(withDictionaryItem ? UPDATE_DICTIONARY_ITEMS : UPDATE_GENERIC_DICTIONARY_ITEMS, {
    onError: () => {
      setPending(false);
    }
  });

  const handleSave = () => {
    const data = lens |> get;
    if (!validate(data)) return;

    const inputItems = data.map(item => {
      const inputItem = { ...item };
      if (inputItem.isNew) {
        inputItem.id = null;
        delete inputItem.isNew;
      }
      delete inputItem.isSystem;
      delete inputItem.originalIndex;
      if (withDictionaryItem) {
        delete inputItem.dictionaryItemDescription;
        delete inputItem.implicit;
        delete inputItem.orderNo;
      }
      return inputItem;
    });

    setPending(true);

    updateDetails({ variables: { input: { dictionaryId, items: inputItems } } });
  };

  useSubscription(DICTIONARY_CHANGED, {
    onData: ({ client }) => {
      const queryName = withDictionaryItem ? 'dictionaryItems' : 'genericDictionaryItems';
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: queryName, args: { dictionaryId } });
      client.cache.gc();
      addToast(t('Dictionaries.SavingSucceeded'), 'success');
      setTimeout(() => {
        setPending(false);
      }, 100);
    }
  });

  // #endregion

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  const disabled = useMemo(
    () => dictionary.isReadOnly || pending || !canRead || ((dictionary.isSystem || !!dictionary.dictionaryId) && !canWrite),
    [dictionary, pending, canRead, canWrite]
  );

  const details = getDetails();

  return (
    <>
      <Prompt when={isDirty} message={t('LeavingWithoutSaving')} />
      {details.map(detailLens => (
        <DictionaryDetailItemComponent
          withDictionaryItem={withDictionaryItem}
          lens={detailLens}
          validation={validation[detailLens.originalIndex |> get]}
          dirtyInfo={dirtyInfo[detailLens.originalIndex |> get]}
          disabled={disabled}
          key={detailLens.id |> get}
        />
      ))}
      {details.length === 0 && (
        <TableRow>
          <TableCell colSpan={columnsLength}>
            <Typography variant='subtitle2' fontStyle='italic' color='textPrimary' textAlign='center'>
              {t('ListBrowser.NoEntries')}
            </Typography>
          </TableCell>
        </TableRow>
      )}
      {getErrors(validation) && (
        <StyledTableRow>
          <TableCell colSpan={columnsLength}>
            <ThemedWrapper color='error' flexDirection='column'>
              {getErrors(validation)
                .split(',')
                .map(error => (
                  <span key={error}>{error}</span>
                ))}
            </ThemedWrapper>
          </TableCell>
        </StyledTableRow>
      )}
      <Portal container={() => document.getElementById('dictionary-details-header-actions')}>
        <IconButton color='transparent' tooltip={t('Tooltips.Dictionaries.AddNewItem')} onClick={handleAdd} disabled={disabled}>
          <AddIcon />
        </IconButton>
        <IconButton color='transparent' tooltip={t('Save')} onClick={handleSave} disabled={disabled || !isDirty}>
          <SaveIcon />
        </IconButton>
      </Portal>
    </>
  );
};

DictionaryDetailListComponent.propTypes = {
  dictionary: PropTypes.object,
  withDictionaryItem: PropTypes.bool,
  columnsLength: PropTypes.number
};

export default DictionaryDetailListComponent;
